// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import { fmtDateTime } from '../utils/timezoneUtils';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import SpeedIcon from '@mui/icons-material/Speed';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WifiIcon from '@mui/icons-material/Wifi';
import HttpIcon from '@mui/icons-material/Http';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import axios from 'axios';

const StargateDataMeter = ({ stargateId, stargateName }) => {
  const { isAdmin, isAuthenticated } = useAuth();
  const [dataMeter, setDataMeter] = useState(null);
  const [history, setHistory] = useState([]);
  const [totalAllTime, setTotalAllTime] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [clearHistoryDialogOpen, setClearHistoryDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const cancelTokenSourceRef = useRef(null);

  // Fetch data meter statistics
  const fetchDataMeter = async (showLoadingIndicator = true) => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    // Cancel any existing request
    if (cancelTokenSourceRef.current) {
      try {
        cancelTokenSourceRef.current.cancel('Operation canceled due to new request');
      } catch (cancelError) {
        console.warn('Error canceling previous request:', cancelError);
      }
    }

    // Create new cancel token
    cancelTokenSourceRef.current = axios.CancelToken.source();

    if (showLoadingIndicator) {
      setLoading(true);
    }

    // Store reference to avoid race conditions
    const currentCancelToken = cancelTokenSourceRef.current;

    try {
      const response = await axiosInstance.get(`/data-meter/stargate/${stargateId}/stats`, {
        cancelToken: currentCancelToken?.token,
      });
      setDataMeter(response.data.current);
      setHistory(response.data.history || []);
      setTotalAllTime(response.data.total_all_time || {});
      setError(null);
    } catch (err) {
      // Don't set error if request was canceled
      if (!axios.isCancel(err)) {
        setError('Failed to load data meter');
        console.error('Error fetching data meter:', err);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Only set up polling if authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    fetchDataMeter();
    // Refresh every 5 seconds for real-time updates (without loading indicator)
    const interval = setInterval(() => {
      // Check if still authenticated before fetching
      if (isAuthenticated) {
        fetchDataMeter(false);
      }
    }, 5000);

    // Listen for logout event to stop polling immediately
    const handleLogout = () => {
      clearInterval(interval);
      setDataMeter(null);
      setHistory([]);
      setTotalAllTime({});
    };

    window.addEventListener('user-logout', handleLogout);
    window.addEventListener('session-timeout', handleLogout);

    return () => {
      clearInterval(interval);
      window.removeEventListener('user-logout', handleLogout);
      window.removeEventListener('session-timeout', handleLogout);
      // Cancel any pending requests
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
        cancelTokenSourceRef.current = null;
      }
    };
  }, [stargateId, isAuthenticated]);

  // Format bytes to human-readable format
  const formatBytes = (bytes) => {
    if (!bytes || typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return fmtDateTime(dateString);
  };

  // Calculate duration between dates
  const calculateDuration = (start, end) => {
    if (!start) return 'N/A';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diff = endDate - startDate;

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Handle reset
  const handleReset = async () => {
    try {
      await axiosInstance.post(`/data-meter/stargate/${stargateId}/reset?archive=true`);
      setResetDialogOpen(false);
      fetchDataMeter(true);
    } catch (err) {
      console.error('Error resetting data meter:', err);
      setError('Failed to reset data meter');
    }
  };

  // Handle clear history
  const handleClearHistory = async () => {
    try {
      await axiosInstance.delete(`/data-meter/stargate/${stargateId}/history`);
      setClearHistoryDialogOpen(false);
      fetchDataMeter(true);
    } catch (err) {
      console.error('Error clearing history:', err);
      setError('Failed to clear history');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <LinearProgress />
          <Typography align="center" sx={{ mt: 2 }}>
            Loading data meter...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SpeedIcon />
              <Typography variant="h6">Data Meter</Typography>
              {/* The meter is always-on; the old Archived/Active ternary was
                  dead logic (the stats endpoint only ever returns the active
                  period). */}
              <Tooltip
                title="Counts dashboard-observed device traffic: management API calls (bodies + a flat 20-byte overhead; uploads count request bytes) and the device's MQTT payloads. WireGuard tunnel telemetry is tracked separately in metrics. Not total WAN usage."
                placement="top"
                arrow
              >
                <Chip label="Active" color="success" size="small" />
              </Tooltip>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchDataMeter(true)} size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              {isAdmin() && (
                <Tooltip title="Reset Meter (archives the current period)">
                  <IconButton onClick={() => setResetDialogOpen(true)} size="small" color="warning">
                    <RestartAltIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View History">
                <IconButton
                  onClick={() => setShowHistory(!showHistory)}
                  size="small"
                  color={showHistory ? 'primary' : 'default'}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <Divider />
        <CardContent>
          {/* Only surface the error when we have nothing to show — a transient
              poll failure with stats on screen would otherwise pop an Alert in
              and out every 5s, shifting the whole card. */}
          {error && !dataMeter && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Current Period Stats */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <WifiIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      MQTT Data
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {formatBytes(dataMeter?.mqtt_bytes_received || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(dataMeter?.mqtt_message_count || 0)} messages
                  </Typography>
                  {dataMeter?.last_mqtt_update && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Last update: {formatDate(dataMeter.last_mqtt_update)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <HttpIcon color="primary" />
                    <Typography variant="subtitle2" color="text.secondary">
                      HTTP Data
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {formatBytes(dataMeter?.http_bytes_received || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(dataMeter?.http_request_count || 0)} requests
                  </Typography>
                  {dataMeter?.last_http_update && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      Last update: {formatDate(dataMeter.last_http_update)}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined" sx={{ background: 'rgba(25, 118, 210, 0.08)' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Current Period Total
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (rotates monthly, or on manual reset)
                    </Typography>
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {formatBytes(dataMeter?.total_bytes_received || 0)}
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Started: {formatDate(dataMeter?.period_start)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Duration: {calculateDuration(dataMeter?.period_start, dataMeter?.period_end)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* All-Time Stats */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Tooltip
                title="Since tracking began. Survives history retention and Clear History. Pre-v1.6.262 baseline is approximate (older versions undercounted MQTT and estimated some HTTP)."
                placement="top-start"
                arrow
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1, width: 'fit-content' }}>
                  All-Time Statistics
                </Typography>
              </Tooltip>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Chip
                  label={`Total: ${formatBytes(totalAllTime.total_bytes_received || 0)}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`MQTT: ${formatBytes(totalAllTime.mqtt_bytes_received || 0)}`}
                  variant="outlined"
                />
                <Chip
                  label={`HTTP: ${formatBytes(totalAllTime.http_bytes_received || 0)}`}
                  variant="outlined"
                />
                <Chip
                  label={`${formatNumber(totalAllTime.mqtt_message_count || 0)} MQTT msgs`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`${formatNumber(totalAllTime.http_request_count || 0)} HTTP reqs`}
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Grid>
          </Grid>

          {/* History Section */}
          {showHistory && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1">Historical Periods</Typography>
                {isAdmin() && history.length > 0 && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => setClearHistoryDialogOpen(true)}
                    startIcon={<DeleteIcon />}
                  >
                    Clear History
                  </Button>
                )}
              </Box>
              {history.length > 0 ? (
                <Stack spacing={1}>
                  {history.map((period, index) => (
                    <Card key={period.id} variant="outlined">
                      <CardContent sx={{ py: 1 }}>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(period.period_start)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Duration: {calculateDuration(period.period_start, period.period_end)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="body2">
                              Total: {formatBytes(period.total_bytes_received)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" color="text.secondary">
                              MQTT: {formatBytes(period.mqtt_bytes_received)}
                              <br />
                              {formatNumber(period.mqtt_message_count)} msgs
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" color="text.secondary">
                              HTTP: {formatBytes(period.http_bytes_received)}
                              <br />
                              {formatNumber(period.http_request_count)} reqs
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info">
                  No historical periods yet. Periods are archived automatically at each
                  month boundary, and whenever an admin resets the meter.
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Data Meter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the data meter for {stargateName}?
            <br />
            <br />
            The current data will be archived and a new tracking period will begin.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} color="warning" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear History Dialog */}
      <Dialog open={clearHistoryDialogOpen} onClose={() => setClearHistoryDialogOpen(false)}>
        <DialogTitle>Clear Historical Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all archived periods for {stargateName}? All-Time
            statistics are kept (they survive history clearing).
            <br />
            <br />
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearHistoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleClearHistory} color="error" variant="contained">
            Clear History
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StargateDataMeter;
